/**
 * The API client. One implementation, used by both clients.
 *
 * Web keeps tokens in memory + localStorage; mobile keeps them in
 * expo-secure-store. That is the only difference, and it is injected as a
 * `TokenStore`. Everything else — refresh-on-401, single-flight refresh,
 * error shapes, route paths — is written once here.
 */
/** In-memory store; useful for tests and as a base for the web store. */
export function memoryTokenStore(initial = null) {
    let tokens = initial;
    return {
        async get() {
            return tokens;
        },
        async set(next) {
            tokens = next;
        },
    };
}
export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
    get isConflict() {
        return this.status === 409;
    }
    get isAuth() {
        return this.status === 401;
    }
    /** Worth retrying later from the offline queue. */
    get isTransient() {
        return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
    }
}
function buildQuery(query) {
    if (!query)
        return '';
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === '')
            continue;
        if (Array.isArray(value)) {
            for (const v of value)
                params.append(key, String(v));
        }
        else {
            params.set(key, String(value));
        }
    }
    const s = params.toString();
    return s ? `?${s}` : '';
}
export class ApiClient {
    baseUrl;
    tokens;
    fetchImpl;
    onUnauthenticated;
    refreshSkewMs;
    refreshInFlight = null;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/, '');
        this.tokens = options.tokens ?? memoryTokenStore();
        this.fetchImpl = options.fetchImpl ?? ((...a) => fetch(...a));
        this.onUnauthenticated = options.onUnauthenticated;
        this.refreshSkewMs = options.refreshSkewMs ?? 30_000;
    }
    /**
     * An access token guaranteed good for the next few minutes.
     *
     * For the one caller that cannot use a header: EventSource. Reading the
     * store directly is not enough — a token sitting there may already have
     * expired, and unlike an ordinary request nothing on the stream path
     * refreshes it. Fifteen minutes into a shift the office's live-call stream
     * would 401 in a reconnect loop until somebody reloaded the page.
     */
    async freshAccessToken() {
        const current = await this.tokens.get();
        if (!current)
            return null;
        if (current.expiresAt - this.refreshSkewMs <= Date.now()) {
            const refreshed = await this.refresh();
            return refreshed?.accessToken ?? null;
        }
        return current.accessToken;
    }
    // -- transport ------------------------------------------------------------
    async authHeader() {
        const current = await this.tokens.get();
        if (!current)
            return {};
        if (current.expiresAt - this.refreshSkewMs <= Date.now()) {
            const refreshed = await this.refresh();
            if (!refreshed)
                return {};
            return { Authorization: `Bearer ${refreshed.accessToken}` };
        }
        return { Authorization: `Bearer ${current.accessToken}` };
    }
    /** Single-flight: twenty parallel queries must not fire twenty refreshes. */
    async refresh() {
        if (this.refreshInFlight)
            return this.refreshInFlight;
        this.refreshInFlight = (async () => {
            const current = await this.tokens.get();
            if (!current?.refreshToken)
                return null;
            try {
                const res = await this.fetchImpl(`${this.baseUrl}/auth/refresh`, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ refreshToken: current.refreshToken }),
                });
                if (!res.ok) {
                    await this.tokens.set(null);
                    this.onUnauthenticated?.();
                    return null;
                }
                const body = (await res.json());
                const pair = {
                    accessToken: body.accessToken,
                    refreshToken: body.refreshToken,
                    expiresAt: Date.now() + body.expiresIn * 1000,
                };
                await this.tokens.set(pair);
                return pair;
            }
            catch {
                return null;
            }
            finally {
                this.refreshInFlight = null;
            }
        })();
        return this.refreshInFlight;
    }
    async request(method, path, options = {}) {
        const url = `${this.baseUrl}${path}${buildQuery(options.query)}`;
        const headers = { ...(await this.authHeader()) };
        let body;
        if (options.formData) {
            body = options.formData;
        }
        else if (options.body !== undefined) {
            headers['content-type'] = 'application/json';
            body = JSON.stringify(options.body);
        }
        let res;
        try {
            res = await this.fetchImpl(url, { method, headers, body });
        }
        catch (cause) {
            // Network down. Callers on mobile turn this into a queued mutation.
            throw new ApiError(0, 'network_error', 'Cannot reach the server', cause);
        }
        if (res.status === 401 && options.retryOn401 !== false) {
            const refreshed = await this.refresh();
            if (refreshed) {
                return this.request(method, path, { ...options, retryOn401: false });
            }
            this.onUnauthenticated?.();
        }
        if (res.status === 204)
            return undefined;
        const text = await res.text();
        let payload = null;
        if (text) {
            try {
                payload = JSON.parse(text);
            }
            catch {
                payload = text;
            }
        }
        if (!res.ok) {
            const err = payload
                ?.error;
            throw new ApiError(res.status, err?.code ?? 'http_error', err?.message ?? `Request failed with ${res.status}`, err?.details);
        }
        return payload;
    }
    get(path, query) {
        return this.request('GET', path, { query });
    }
    post(path, body, query) {
        return this.request('POST', path, { body, query });
    }
    patch(path, body) {
        return this.request('PATCH', path, { body });
    }
    del(path) {
        return this.request('DELETE', path);
    }
    /**
     * A response that is not JSON.
     *
     * The print view and the PDF are behind the same auth as everything else, so
     * they cannot be an `<a href>` or an `<iframe src>` — a browser sends no
     * Authorization header on either. They are fetched with the token like any
     * other call and handed to the page as text or a blob.
     */
    async raw(path) {
        const headers = await this.authHeader();
        const res = await this.fetchImpl(`${this.baseUrl}${path}`, { method: 'GET', headers });
        if (res.status === 401) {
            const refreshed = await this.refresh();
            if (refreshed) {
                return this.fetchImpl(`${this.baseUrl}${path}`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${refreshed.accessToken}` },
                });
            }
            this.onUnauthenticated?.();
        }
        if (!res.ok) {
            const text = await res.text();
            let message = `Request failed with ${res.status}`;
            try {
                message = JSON.parse(text)?.error?.message ?? message;
            }
            catch {
                /* not JSON; keep the status message */
            }
            throw new ApiError(res.status, 'http_error', message);
        }
        return res;
    }
    /** Multipart POST — `request` already knows not to set a content-type for it. */
    postForm(path, formData) {
        return this.request('POST', path, { formData });
    }
    // -- auth ------------------------------------------------------------------
    auth = {
        login: async (email, password, device) => {
            const res = await this.post('/auth/login', { email, password, device });
            await this.tokens.set({
                accessToken: res.accessToken,
                refreshToken: res.refreshToken,
                expiresAt: Date.now() + res.expiresIn * 1000,
            });
            return res;
        },
        /**
         * Start a company and sign straight in.
         *
         * Stores the tokens exactly as login does, because from the client's point
         * of view that is what happened: there is now a session.
         */
        signup: async (input) => {
            const res = await this.post('/auth/signup', input);
            await this.tokens.set({
                accessToken: res.accessToken,
                refreshToken: res.refreshToken,
                expiresAt: Date.now() + res.expiresIn * 1000,
            });
            return res;
        },
        /**
         * Ask for a reset link.
         *
         * Succeeds whether or not the address has an account — the server answers
         * identically on purpose, so the caller learns nothing it should not.
         */
        forgotPassword: (email) => this.post('/auth/forgot', { email }),
        me: () => this.get('/auth/me'),
        logout: async () => {
            const current = await this.tokens.get();
            try {
                if (current?.refreshToken) {
                    await this.post('/auth/logout', { refreshToken: current.refreshToken });
                }
            }
            finally {
                await this.tokens.set(null);
            }
        },
        forceRefresh: () => this.refresh(),
    };
    // -- users -----------------------------------------------------------------
    users = {
        list: (query) => this.get('/users', query),
        /**
         * Everybody who can be given a job: the technicians, plus any office
         * person somebody has ticked "goes on jobs" for. Named `techs` still
         * because that is what every screen calls the list it feeds.
         */
        techs: () => this.get('/users', { assignable: true }),
        // -- the team. Admin only; the server refuses the rest.
        create: (input) => this.post('/users', input),
        update: (id, input) => this.patch(`/users/${id}`, input),
        /** Ends their access and revokes their sessions. The records keep their name. */
        deactivate: (id) => this.post(`/users/${id}/deactivate`),
        reactivate: (id) => this.post(`/users/${id}/reactivate`),
    };
    // -- customers & properties -------------------------------------------------
    customers = {
        list: (query) => this.get('/customers', query),
        get: (id) => this.get(`/customers/${id}`),
        create: (input) => this.post('/customers', input),
        update: (id, input) => this.patch(`/customers/${id}`, input),
        remove: (id) => this.del(`/customers/${id}`),
        bulkRemove: (ids) => this.post('/customers/bulk-delete', { ids }),
        addProperty: (customerId, input) => this.post(`/customers/${customerId}/properties`, input),
        updateProperty: (id, input) => this.patch(`/properties/${id}`, input),
        removeProperty: (id) => this.del(`/properties/${id}`),
    };
    /**
     * Bulk import. The rows are built in the browser by `csv-import.ts`, so what
     * crosses the wire is already typed and already complained about.
     */
    imports = {
        customers: (rows) => this.post('/imports/customers', { rows }),
        jobs: (rows) => this.post('/imports/jobs', { rows }),
    };
    // -- price book ------------------------------------------------------------
    priceBook = {
        list: (q) => this.get('/pricebook', { q }),
        create: (input) => this.post('/pricebook', input),
        update: (id, input) => this.patch(`/pricebook/${id}`, input),
        remove: (id) => this.del(`/pricebook/${id}`),
    };
    // -- jobs ------------------------------------------------------------------
    jobs = {
        list: (query) => this.get('/jobs', query),
        get: (id) => this.get(`/jobs/${id}`),
        create: (input) => this.post('/jobs', input),
        update: (id, input) => this.patch(`/jobs/${id}`, input),
        remove: (id) => this.del(`/jobs/${id}`),
        bulkRemove: (ids) => this.post('/jobs/bulk-delete', { ids }),
        setStatus: (id, status, opts) => this.post(`/jobs/${id}/status`, { status, ...opts }),
        enRoute: (id, clientCreatedAt) => this.post(`/jobs/${id}/en-route`, { clientCreatedAt }),
        schedule: (id, input) => this.post(`/jobs/${id}/schedule`, input),
        checkConflicts: (query) => this.get('/jobs/conflicts', query),
        /** Multipart, same route the phone posts photos to. */
        addAttachment: (id, file, kind = 'document') => {
            const form = new FormData();
            form.append('kind', kind);
            form.append('file', file, file instanceof File ? file.name : 'upload');
            return this.postForm(`/jobs/${id}/attachments`, form);
        },
        /** The internal cost sheet. Office only; a technician gets a 403. */
        costItems: (id) => this.get(`/jobs/${id}/costs`),
        addCostItem: (id, input) => this.post(`/jobs/${id}/costs`, input),
        removeCostItem: (id, costItemId) => this.del(`/jobs/${id}/costs/${costItemId}`),
        setLineItems: (id, lineItems) => this.request('PUT', `/jobs/${id}/line-items`, { body: { lineItems } }),
        addNote: (id, body, visibility = 'internal', clientCreatedAt) => this.post(`/jobs/${id}/notes`, { body, visibility, clientCreatedAt }),
        uploadAttachment: (id, file, opts) => {
            const form = new FormData();
            form.append('file', file, file.name ?? 'upload.jpg');
            if (opts?.kind)
                form.append('kind', opts.kind);
            if (opts?.caption)
                form.append('caption', opts.caption);
            return this.request('POST', `/jobs/${id}/attachments`, { formData: form });
        },
        removeAttachment: (jobId, attachmentId) => this.del(`/jobs/${jobId}/attachments/${attachmentId}`),
    };
    // -- estimates -------------------------------------------------------------
    estimates = {
        list: (query) => this.get('/estimates', query),
        get: (id) => this.get(`/estimates/${id}`),
        create: (input) => this.post('/estimates', input),
        update: (id, input) => this.patch(`/estimates/${id}`, input),
        setStatus: (id, status) => this.post(`/estimates/${id}/status`, { status }),
        convert: (id, input) => this.post(`/estimates/${id}/convert`, input ?? {}),
        /** Email it to the customer, with a printable copy attached as a PDF. */
        email: (id, to) => this.post(`/estimates/${id}/email`, to ? { to } : {}),
        remove: (id) => this.del(`/estimates/${id}`),
        bulkRemove: (ids) => this.post('/estimates/bulk-delete', { ids }),
        /** The same two, for the same reason, on the estimate. */
        printHtml: (id) => this.raw(`/estimates/${id}/print`).then((r) => r.text()),
        pdf: (id) => this.raw(`/estimates/${id}/pdf`).then((r) => r.blob()),
    };
    // -- invoices & payments ---------------------------------------------------
    invoices = {
        list: (query) => this.get('/invoices', query),
        get: (id) => this.get(`/invoices/${id}`),
        createFromJob: (jobId, input) => this.post('/invoices', { jobId, ...input }),
        update: (id, input) => this.patch(`/invoices/${id}`, input),
        send: (id) => this.post(`/invoices/${id}/send`),
        /** Email it to the customer, with a printable copy attached as a PDF. */
        email: (id, to) => this.post(`/invoices/${id}/email`, to ? { to } : {}),
        void: (id) => this.post(`/invoices/${id}/void`),
        remove: (id) => this.del(`/invoices/${id}`),
        bulkRemove: (ids) => this.post('/invoices/bulk-delete', { ids }),
        /**
         * The customer's copy as HTML, for the preview.
         *
         * `printUrl` is kept for nothing but display: opening it directly sends no
         * token and 401s, which is how the old Print button was quietly broken.
         */
        printHtml: (id) => this.raw(`/invoices/${id}/print`).then((r) => r.text()),
        pdf: (id) => this.raw(`/invoices/${id}/pdf`).then((r) => r.blob()),
        printUrl: (id) => `${this.baseUrl}/invoices/${id}/print`,
        addPayment: (id, input) => this.post(`/invoices/${id}/payments`, input),
        voidPayment: (invoiceId, paymentId) => this.post(`/invoices/${invoiceId}/payments/${paymentId}/void`),
    };
    // -- lead sources ----------------------------------------------------------
    /** Lead sources and job types — one API, told apart by `kind`. */
    catalog = {
        list: (kind, includeArchived = false) => this.get('/catalog', { kind, ...(includeArchived ? { includeArchived: 'true' } : {}) }),
        create: (input) => this.post('/catalog', input),
        rename: (id, name) => this.patch(`/catalog/${id}`, { name }),
        /** Hides it from the pickers. Jobs already tagged with it keep the label. */
        remove: (id) => this.post(`/catalog/${id}/remove`),
        restore: (id) => this.post(`/catalog/${id}/restore`),
    };
    /** One box for the whole system: customers, jobs, invoices, estimates. */
    search = (q) => this.get('/search', { q });
    // -- settings --------------------------------------------------------------
    settings = {
        /** What mailbox, if any, this company sends from. Never returns tokens. */
        email: () => this.get('/settings/email'),
        /** Returns the Google consent URL for the browser to visit. */
        connectGmail: () => this.post('/settings/email/google/start'),
        disconnectMailbox: () => this.del('/settings/email'),
        /** Sends to the signed-in user's own address, and nowhere else. */
        sendTestEmail: () => this.post('/settings/email/test'),
        /** The letterhead every document header is built from. */
        company: () => this.get('/settings/company'),
        updateCompany: (input) => this.patch('/settings/company', input),
        /**
         * Multipart, because a logo is a file. Deliberately not JSON-with-base64:
         * the same size limit, virus surface and storage path as every other
         * upload should apply, and they already exist for attachments.
         */
        uploadLogo: (file, filename = 'logo.png') => {
            const form = new FormData();
            form.append('file', file, filename);
            return this.postForm('/settings/company/logo', form);
        },
        removeLogo: () => this.del('/settings/company/logo'),
    };
    // -- connecting a card-payment account -------------------------------------
    /**
     * The company's own Stripe account. Their customers' cards settle to them;
     * the platform takes a fee. Nothing here ever carries identity data — the
     * onboarding form is Stripe's, rendered inside our page.
     */
    paymentsAccount = {
        get: () => this.get('/payments/account'),
        /** Creates it if there is none; returns the existing one otherwise. */
        create: () => this.post('/payments/account'),
        /** A short-lived secret for the embedded onboarding component. */
        session: () => this.post('/payments/account-session'),
        /** Ask Stripe directly, for when a webhook was missed. */
        refresh: () => this.post('/payments/account/refresh'),
        /**
         * Forget a half-finished account and start again.
         *
         * Refused once the account has ever charged — at that point it may hold
         * money, and forgetting its id would make it unreachable rather than gone.
         */
        forget: () => this.del('/payments/account'),
    };
    // -- the company's own phone line ------------------------------------------
    phone = {
        status: () => this.get('/telephony/phone'),
        search: (areaCode) => this.get('/telephony/numbers', { areaCode }),
        buy: (number) => this.post('/telephony/phone', { number }),
        /** E.164, or null to stop forwarding unanswered calls. */
        setForwarding: (forwardTo) => this.patch('/telephony/phone/forwarding', { forwardTo }),
    };
    // -- the platform owner's own screen ---------------------------------------
    //
    // Every one of these 404s for anybody who is not Crewlark staff, checked
    // against the database rather than the token. See auth/platform.ts.
    admin = {
        companies: () => this.get('/admin/companies'),
        extendTrial: (id, days) => this.post(`/admin/companies/${id}/extend-trial`, { days }),
        suspend: (id, reason) => this.post(`/admin/companies/${id}/suspend`, { reason }),
        unsuspend: (id) => this.post(`/admin/companies/${id}/unsuspend`),
        /** Switch the dialer tier on or off. Never touches their number or calls. */
        setTelephony: (id, enabled) => this.post(`/admin/companies/${id}/telephony`, { enabled }),
    };
    // -- what this company pays Crewlark ---------------------------------------
    //
    // Not to be confused with `payments`/`paymentsAccount` above, which are how
    // this company gets paid BY ITS OWN customers. These three are how Crewlark
    // gets paid by this company.
    billing = {
        /** Whether the server has prices configured at all. */
        status: () => this.get('/billing/status'),
        /** Returns a Stripe Checkout URL to send the browser to. */
        checkout: () => this.post('/billing/checkout'),
        /** Stripe's hosted page for changing a card or cancelling. */
        portal: () => this.post('/billing/portal'),
    };
    // -- recurrences (P1) ------------------------------------------------------
    recurrences = {
        list: () => this.get('/recurrences'),
        create: (input) => this.post('/recurrences', input),
        update: (id, input) => this.patch(`/recurrences/${id}`, input),
        remove: (id) => this.del(`/recurrences/${id}`),
        generate: (id, horizonDays = 60) => this.post(`/recurrences/${id}/generate`, { horizonDays }),
        generateAll: (horizonDays = 60) => this.post('/recurrences/generate', { horizonDays }),
    };
    // -- reports (P1) ----------------------------------------------------------
    reports = {
        revenue: (months = 12) => this.get('/reports/revenue', { months }),
        jobsByStatus: (query) => this.get('/reports/jobs-by-status', query),
        receivables: () => this.get('/reports/receivables'),
    };
    // -- telephony -------------------------------------------------------------
    telephony = {
        capabilities: () => this.get('/telephony/capabilities'),
        /**
         * A Twilio voice access token for this user's browser. Refused with a 422
         * when the company has no number, which the softphone reads as "not set
         * up" rather than as an error.
         */
        token: () => this.get('/telephony/token'),
    };
    calls = {
        list: (query) => this.get('/calls', query),
        /** Ringing or connected right now. The screen-pop falls back to this if
         *  the event stream drops. */
        live: () => this.get('/calls/live'),
        get: (id) => this.get(`/calls/${id}`),
        context: (id) => this.get(`/calls/${id}/context`),
        update: (id, input) => this.patch(`/calls/${id}`, input),
        clickToCall: (input) => this.post('/calls/click-to-call', input),
        /**
         * URL for an EventSource. The access token travels in the query string
         * because EventSource cannot set headers; see the route for the trade-off.
         */
        streamUrl: (accessToken) => `${this.baseUrl}/calls/stream?access_token=${encodeURIComponent(accessToken)}`,
    };
    // -- taking payment --------------------------------------------------------
    payments = {
        /** What the processor can do, so the UI never offers what it cannot. */
        capabilities: () => this.get('/payments/capabilities'),
        /**
         * Everything a client needs before it can put a card field on screen.
         *
         * One request rather than three, because the field app asks this at a
         * customer's door on whatever signal a driveway has. The key and the
         * account id come back null for anybody who may not charge — see the
         * route.
         */
        terminal: () => this.get('/payments/terminal'),
    };
    paymentRequests = {
        list: (query) => this.get('/payment-requests', query),
        get: (id) => this.get(`/payment-requests/${id}`),
        create: (input) => this.post('/payment-requests', input),
        /** Text the link to the customer. */
        send: (id, toNumber) => this.post(`/payment-requests/${id}/send`, toNumber ? { toNumber } : {}),
        /** Ask the processor again, for when a webhook went missing. */
        refresh: (id) => this.post(`/payment-requests/${id}/refresh`, {}),
        cancel: (id) => this.post(`/payment-requests/${id}/cancel`, {}),
        charge: (id, token) => this.post(`/payment-requests/${id}/charge`, { token }),
        /** Development only; absent in production by construction. */
        testSettle: (id, outcome = 'paid') => this.post(`/payment-requests/${id}/test-settle`, { outcome }),
    };
    messages = {
        list: (query) => this.get('/messages', query),
        send: (input) => this.post('/messages', input),
    };
    // -- devices & sync --------------------------------------------------------
    devices = {
        register: (input) => this.post('/devices', input),
        unregister: (pushToken) => this.post('/devices/unregister', { pushToken }),
    };
    sync = {
        pull: (days = 2, back = 0) => this.get('/sync/pull', { days, back }),
        push: (mutations) => this.post('/sync/push', {
            mutations: mutations.map((m) => ({
                id: m.id,
                seq: m.seq,
                kind: m.kind,
                jobId: m.jobId,
                createdAt: m.createdAt,
                payload: m.payload,
            })),
        }),
    };
    // -- public (no auth) ------------------------------------------------------
    publicStatus = (token) => this.request('GET', `/public/jobs/${token}`, { retryOn401: false });
}
export function createApiClient(options) {
    return new ApiClient(options);
}
//# sourceMappingURL=api-client.js.map